import { API, Auth } from "aws-amplify";
import { fetchCancellationRows } from "@/api/cancellationsQueue";
import { needsDecision, CANCELLATION_CUTOFF_AT } from "@/utils/omsCancellations";

// `failed` is the badge's third state: the newest attempt to count could not
// load. It is not a count, so the badge never shows a number it cannot back
// (OPT-71). `loaded` stays false until a first count lands.
const initialState = () => ({ unresolved: 0, loaded: false, failed: false });
const state = initialState();

// Newest wins. The layout's fetch and the Cancellations page's load run
// independently; whichever STARTED last is the only one allowed to commit, so
// a slow older response cannot overwrite what the page just showed.
let latest = 0;

const mutations = {
  SET_UNRESOLVED(state, n) {
    state.unresolved = n;
    state.loaded = true;
    state.failed = false;
  },
  SET_CANCELLATIONS_UNAVAILABLE(state) {
    state.failed = true;
  },
  // The root RESET (sign-out) runs this too, so the next user never sees the
  // previous user's count.
  RESET(state) {
    latest += 1;
    Object.assign(state, initialState());
  },
};

export function countNeedingDecision(rows) {
  const cutoff = Date.parse(CANCELLATION_CUTOFF_AT);
  return rows.filter((r) => needsDecision(r, cutoff)).length;
}

const actions = {
  // Named refreshCancellations, not refresh, so it cannot collide with a
  // root-level action (see BINDING AMENDMENT SA8).
  async refreshCancellations({ commit }) {
    const ticket = ++latest;
    let n;
    try {
      const user = await Auth.currentAuthenticatedUser();
      n = countNeedingDecision(await fetchCancellationRows(API, user.username));
    } catch (e) {
      if (ticket === latest) commit("SET_CANCELLATIONS_UNAVAILABLE");
      throw e;
    }
    if (ticket === latest) commit("SET_UNRESOLVED", n);
  },

  // The Cancellations page starts a load: take a ticket now, so a response
  // that arrives after a newer load, a newer layout fetch or a sign-out is
  // dropped instead of overwriting the badge.
  beginCancellationsLoad() {
    return ++latest;
  },

  // The page loaded these rows: the badge is the count of the rows the
  // "Needs decision" view is built from, so the two cannot disagree.
  setCancellationsFromRows({ commit }, { rows, ticket }) {
    if (ticket === latest) commit("SET_UNRESOLVED", countNeedingDecision(rows));
  },

  // The page could not load its rows: show that, not a number.
  markCancellationsUnavailable({ commit }, { ticket }) {
    if (ticket === latest) commit("SET_CANCELLATIONS_UNAVAILABLE");
  },
};

const getters = {
  // Stays a number: PickList's banner and the nav's generic routeBadge read it.
  cancellationsBadge: (state) => (state.loaded && !state.failed ? state.unresolved : 0),
  cancellationsUnavailable: (state) => !!state.failed,
};

export default { namespaced: false, state, mutations, actions, getters };
